/**
 * Did we already try to update, and did it achieve nothing?
 *
 * THE LOOP THIS EXISTS TO KILL. "Actualizar" runs `forceUpdate()`, which
 * unregisters the service workers, clears the caches and reloads. That is the
 * right thing to do — and it is powerless when the server has no newer bundle
 * to give. You reload, land on the identical build, the version check compares
 * the same two numbers, and the button comes back. Forever.
 *
 * It is not hypothetical: reported on clubmanager DEV 2026-08-10, and
 * `product-page.tsx` already guarded a narrow slice of it with
 * `import.meta.env.DEV` — correct reasoning, wrong condition. `DEV` is only
 * true on a local Vite server, so a built bundle on dev HOSTING skipped the
 * guard entirely and looped. The real question was never "which build mode is
 * this" but "can a reload actually change anything".
 *
 * So: stamp the version we were on when the user asked, before reloading. If
 * we come back still on that version, the reload did not help and repeating it
 * will not either — say so instead of offering the button again.
 *
 * `sessionStorage` on purpose: the stamp must not outlive the tab. A user who
 * closes the app and returns tomorrow, after a real deploy, deserves a clean
 * attempt rather than a memory of yesterday's failure.
 *
 * usersmatch carries the same contract in
 * `apps/usersmatch-pwa/src/core/firebase/update-attempt.ts`. Kept as twins
 * rather than shared code because clubmanager does not depend on
 * `@upadelmatch/core` today; moving both there is the tidier end state and a
 * build-config change, not a behaviour one.
 */

const KEY = 'upm:update-attempt'

/** Record that the user asked to update while running `version`. */
export function markUpdateAttempt(version: string): void {
  try {
    sessionStorage.setItem(KEY, version)
  } catch {
    // Safari private mode throws on setItem. Losing the stamp only costs the
    // user one more useless click — never break the update path over it.
  }
}

/**
 * True when we already tried to update FROM this exact version, i.e. the
 * reload happened and changed nothing. A second attempt cannot do better.
 */
export function updateAlreadyAttempted(version: string): boolean {
  try {
    return sessionStorage.getItem(KEY) === version
  } catch {
    return false
  }
}

/**
 * Call once the running version differs from the stamp — the update worked, so
 * the memory of the failed attempt has to go or the next genuine update would
 * be suppressed.
 */
export function clearUpdateAttemptIfMoved(version: string): void {
  try {
    const stamped = sessionStorage.getItem(KEY)
    if (stamped !== null && stamped !== version) sessionStorage.removeItem(KEY)
  } catch {
    /* see markUpdateAttempt */
  }
}
